// Mirrors the public PdfPreviewSnapshot and the editable fields in PdfConfirmRequest.
export type PdfPreviewSource = {
  pageNumber: number;
  regionId: string | null;
  artifactId: string | null;
};
export type PdfPreviewField = {
  state:
    | 'confirmed'
    | 'missing'
    | 'unsupported'
    | 'invalid_evidence'
    | 'ambiguous_association'
    | 'visual_only'
    | 'conflicting';
  value: unknown;
  suggestions: {
    value: unknown;
    state: 'visual_only' | 'conflicting' | 'unsupported';
    sources: PdfPreviewSource[];
  }[];
  sources: PdfPreviewSource[];
  needsReview: boolean;
};
export type PdfPreviewLocation = {
  status: 'matched' | 'not_found' | 'ambiguous' | 'conflicting' | 'no_input';
  locationId: string | null;
  nameEn: string | null;
  hierarchy: {
    city: string | null;
    level1: string | null;
    level2: string | null;
    level3: string | null;
  } | null;
  candidateIds: string[];
  reviewCodes: string[];
};
export type PdfPreviewReview = {
  code: string;
  field: string;
  projectKey: string | null;
  unitKey: string | null;
  sources: PdfPreviewSource[];
};
export type PdfPreviewUnit = {
  unitKey: string;
  candidateRef: string;
  title: string;
  fields: Record<string, PdfPreviewField>;
  location: PdfPreviewLocation;
  mediaSources: PdfPreviewSource[];
  recommendedMediaIds?: string[];
  reviewMediaIds?: string[];
  excludedMediaIds?: string[];
  review: PdfPreviewReview[];
};
export type PdfPreviewProject = {
  projectKey: string;
  sharedProject: Omit<PdfPreviewUnit, 'unitKey'>;
  units: PdfPreviewUnit[];
};
export type PdfPreviewMedia = {
  mediaId: string;
  kind:
    | 'property_photo'
    | 'floorplan'
    | 'map'
    | 'logo'
    | 'icon'
    | 'decorative'
    | 'document_screenshot'
    | 'unknown';
  status: 'prepared' | 'excluded' | 'unavailable';
  width: number;
  height: number;
  needsReview: boolean;
  reviewReasons: string[];
  association: {
    scope: 'project' | 'unit' | 'unassigned';
    candidateRef: string | null;
    reason: string;
  };
  occurrences: {
    occurrenceRef: string;
    pageNumber: number;
    imageNumber: number;
    regionId: string | null;
  }[];
  proposedKind?: PdfPreviewMedia['kind'] | null;
  classificationSource?: 'unclassified' | 'luna_suggestion' | 'conflicting_suggestions';
  placements?: {
    placementRef: string;
    pageNumber: number;
    sourceRect: { x: number; y: number; width: number; height: number };
    geometryStatus: 'verified' | 'clipped';
    gallerySectionId?: string | null;
    sourceSectionId?: string | null;
    ownershipStatus?: 'verified' | 'ambiguous' | 'unassigned';
    overlappingSectionIds: string[];
    proposedKind: PdfPreviewMedia['kind'] | null;
    proposedAssociation: {
      scope: 'project' | 'unit' | 'unresolved';
      candidateRef: string | null;
    } | null;
    reviewReasons: string[];
  }[];
};
export type PdfPreviewSnapshot = {
  version: 'pdf-preview-v1' | 'pdf-preview-v2' | 'pdf-preview-v3';
  previewId: string;
  creatorId: string;
  sourceJobId: string;
  sourceId: string;
  sourceSha256: string;
  pipelineVersion: string;
  schemaVersion: string;
  processingStatus: 'completed' | 'partial';
  finishedAt: string;
  expiresAt: string;
  requiresPartialAcknowledgment: boolean;
  projects: PdfPreviewProject[];
  ungroupedUnits: PdfPreviewUnit[];
  possibleDuplicates: { leftUnitKey: string; rightUnitKey: string; basis: string }[];
  unresolvedSectionIds: string[];
  sourceObservations: {
    kind: 'non_property' | 'incomplete' | 'uncertain';
    sources: PdfPreviewSource[];
  }[];
  mediaSources: PdfPreviewSource[];
  media?: PdfPreviewMedia[];
  unassignedMediaIds?: string[];
  mediaSuggestions?: {
    sourceIndex: number;
    page: number | null;
    kind: 'property_photo' | 'floorplan' | 'map' | 'logo' | 'icon' | 'unknown';
    position: string;
    description: string | null;
    proposedListingRef: string | null;
    candidateRef: string | null;
    possibleDuplicateOf: number | null;
    needsReview: boolean;
    reviewCodes: string[];
  }[];
  review: PdfPreviewReview[];
  coverage: {
    pageCount: number;
    entries: {
      id: string;
      kind: 'page' | 'text_section' | 'visual_region' | 'chunk';
      pageNumbers: number[];
      chunkIds: string[];
      state:
        | 'not_planned'
        | 'planned'
        | 'processing'
        | 'completed'
        | 'failed'
        | 'deferred'
        | 'skipped_with_reason';
      reason: string | null;
    }[];
    documentComplete: boolean;
    recognitionGuaranteed: false;
  };
  incomplete: boolean;
  incompleteBusinessData: boolean;
};

export const PDF_AMENITY_FIELDS = [
  'balcony',
  'maidsRoom',
  'builtInWardrobes',
  'childrenPlayArea',
  'sharedPool',
  'sharedSpa',
  'sharedGym',
  'study',
  'centralAc',
  'concierge',
  'coveredParking',
  'barbecueArea',
  'petsAllowed',
  'publicParking',
  'kitchenAppliances',
  'lobbyInBuilding',
  'maidService',
  'petsNotAllowed',
  'privateGarden',
  'privateGym',
  'privatePool',
  'security',
  'tennisCourts',
  'privateSpa',
  'laundryFacility',
] as const;
export const PDF_SHARED_FIELDS = [
  'title',
  'locationId',
  'completionStatus',
  'handoverQuarter',
  'handoverYear',
  'postHandover',
  'furniture',
  'view',
  ...PDF_AMENITY_FIELDS,
] as const;
export const PDF_UNIT_FIELDS = [
  'activityKind',
  'propertyKind',
  'transactionType',
  'completionStatus',
  'furniture',
  'floor',
  'beds',
  'baths',
  'currency',
  'defaultRentFrequency',
  'handoverQuarter',
  'title',
  'description',
  'builtUpArea',
  'plotArea',
  'price',
  'sellingPrice',
  'priceDaily',
  'priceWeekly',
  'priceMonthly',
  'priceYearly',
  'locationId',
  'postHandover',
  'handoverYear',
  'view',
  ...PDF_AMENITY_FIELDS,
] as const;
export type PdfConfirmFieldName = (typeof PDF_UNIT_FIELDS)[number];
export type PdfConfirmFieldValue = string | number | boolean | string[] | null;
export type PdfConfirmFields = Partial<Record<PdfConfirmFieldName, PdfConfirmFieldValue>>;
export type PdfConfirmAction = 'editor' | 'draft' | 'skip';
export type PdfConfirmRequest = {
  projectOverrides: { projectKey: string; fields: PdfConfirmFields }[];
  decisions: (
    | { unitKey: string; action: 'skip' }
    | {
        unitKey: string;
        action: 'editor' | 'draft';
        fields: PdfConfirmFields;
        selectedMediaIds: string[];
      }
  )[];
  acknowledgePartial: boolean;
  replaceExistingInProgress?: {
    approved: true;
    existingPropertyAdId: string;
    expectedVersion: number;
  };
};
export type PdfConfirmResult = {
  confirmId: string;
  previewId: string;
  status: 'processing' | 'completed' | 'cleanup_pending' | 'failed';
  errorCode?: string;
  units: {
    unitKey: string;
    action: PdfConfirmAction;
    propertyAdId: string | null;
    mediaCount: number;
  }[];
  editorPropertyAdId: string | null;
  createdDraftIds: string[];
  skippedUnitKeys: string[];
};
export type PdfPreviewEdits = {
  projectOverrides: Record<string, PdfConfirmFields>;
  units: Record<string, { fields: PdfConfirmFields; selectedMediaIds: string[] }>;
  actions: Record<string, PdfConfirmAction>;
  acknowledgePartial: boolean;
};
